import React, { useState, useMemo, useContext } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Button,
  Card,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Chip,
  alpha,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import BusinessIcon from '@mui/icons-material/Business';
import axiosInstance from '../../lib/axios';
import { TableToolbar, FilterOption, ColumnConfig } from '../../components/TableToolbar';
import { ColorModeContext } from '../../contexts/ColorModeContext';
import { palette, typography, theme } from '../../theme/colors';
import { useAnimationStyles } from '../../hooks/useAnimations';
import { useSnackbar } from '../../components/SnackbarProvider';
import { B2bAccessRequest, B2bAccessRequestStatus, PricingTier } from '../../types/b2b';

const fetchAccessRequests = async () => {
  const response = await axiosInstance.get('/access-requests');
  return Array.isArray(response.data) ? response.data : response.data.data || [];
};

const fetchPricingTiers = async () => {
  const response = await axiosInstance.get('/pricing-tiers');
  return Array.isArray(response.data) ? response.data : response.data.data || [];
};

const filterOptions: FilterOption[] = [
  {
    field: 'status',
    label: 'Status',
    type: 'select',
    options: [
      { value: 'pending', label: 'Pending' },
      { value: 'approved', label: 'Approved' },
      { value: 'rejected', label: 'Rejected' },
    ],
  },
];

const exportColumns: ColumnConfig[] = [
  { field: 'companyName', headerName: 'Company' },
  {
    field: 'name',
    headerName: 'Contact Name',
    valueFormatter: (_, row) => `${row.firstName} ${row.lastName}`,
  },
  { field: 'email', headerName: 'Email' },
  { field: 'phone', headerName: 'Phone' },
  { field: 'status', headerName: 'Status' },
  { field: 'createdAt', headerName: 'Submitted' },
];

const getStatusColor = (status: B2bAccessRequestStatus): string => {
  switch (status) {
    case 'pending':
      return palette.warning;
    case 'approved':
      return palette.success;
    case 'rejected':
      return palette.error;
    default:
      return palette.textMuted;
  }
};

const getStatusIcon = (status: B2bAccessRequestStatus): React.ReactElement | undefined => {
  switch (status) {
    case 'pending':
      return <HourglassEmptyIcon sx={{ fontSize: 16 }} />;
    case 'approved':
      return <CheckCircleIcon sx={{ fontSize: 16 }} />;
    case 'rejected':
      return <CancelIcon sx={{ fontSize: 16 }} />;
    default:
      return undefined;
  }
};

export const AccessRequestList = () => {
  const queryClient = useQueryClient();
  const { mode } = useContext(ColorModeContext);
  const { showMessage } = useSnackbar();
  const animations = useAnimationStyles();

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [searchValue, setSearchValue] = useState('');
  const [activeFilters, setActiveFilters] = useState<Record<string, string>>({});

  // Approval dialog state
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<B2bAccessRequest | null>(null);
  const [selectedTierId, setSelectedTierId] = useState('');
  const [reviewNotes, setReviewNotes] = useState('');

  const { data: accessRequests, isLoading } = useQuery({
    queryKey: ['access-requests'],
    queryFn: fetchAccessRequests,
  });

  const { data: pricingTiers } = useQuery({
    queryKey: ['pricing-tiers'],
    queryFn: fetchPricingTiers,
  });

  const approveMutation = useMutation({
    mutationFn: ({ id, tierId, notes }: { id: string; tierId: string; notes?: string }) =>
      axiosInstance.post(`/access-requests/${id}/approve`, { tierId, notes }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['access-requests'] });
      showMessage('Access request approved successfully', 'success');
      handleCloseDialogs();
    },
    onError: (error: any) => {
      showMessage(error.response?.data?.message || 'Failed to approve request', 'error');
    },
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      axiosInstance.post(`/access-requests/${id}/reject`, { reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['access-requests'] });
      showMessage('Access request rejected', 'success');
      handleCloseDialogs();
    },
    onError: (error: any) => {
      showMessage(error.response?.data?.message || 'Failed to reject request', 'error');
    },
  });

  const filteredData = useMemo(() => {
    if (!accessRequests) return [];

    return accessRequests.filter((item: B2bAccessRequest) => {
      // Search filter
      if (searchValue) {
        const search = searchValue.toLowerCase();
        const fullName = `${item.firstName} ${item.lastName}`;
        const matchesSearch =
          item.companyName.toLowerCase().includes(search) ||
          fullName.toLowerCase().includes(search) ||
          item.email.toLowerCase().includes(search);
        if (!matchesSearch) return false;
      }

      // Status filter
      if (activeFilters.status && item.status !== activeFilters.status) {
        return false;
      }

      return true;
    });
  }, [accessRequests, searchValue, activeFilters]);

  const paginatedData = useMemo(() => {
    const startIndex = page * pageSize;
    return filteredData.slice(startIndex, startIndex + pageSize);
  }, [filteredData, page, pageSize]);

  const handleFilterChange = (field: string, value: string) => {
    setActiveFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleClearFilters = () => {
    setActiveFilters({});
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenApproveDialog = (request: B2bAccessRequest) => {
    setSelectedRequest(request);
    setSelectedTierId('');
    setReviewNotes('');
    setApproveDialogOpen(true);
  };

  const handleOpenRejectDialog = (request: B2bAccessRequest) => {
    setSelectedRequest(request);
    setReviewNotes('');
    setRejectDialogOpen(true);
  };

  const handleCloseDialogs = () => {
    setApproveDialogOpen(false);
    setRejectDialogOpen(false);
    setSelectedRequest(null);
    setSelectedTierId('');
    setReviewNotes('');
  };

  const handleApprove = () => {
    if (!selectedRequest || !selectedTierId) return;
    approveMutation.mutate({
      id: selectedRequest.id,
      tierId: selectedTierId,
      notes: reviewNotes || undefined,
    });
  };

  const handleReject = () => {
    if (!selectedRequest) return;
    rejectMutation.mutate({
      id: selectedRequest.id,
      reason: reviewNotes || undefined,
    });
  };

  // Calculate statistics
  const stats = useMemo(() => {
    const total = accessRequests?.length || 0;
    const pending = accessRequests?.filter((r: B2bAccessRequest) => r.status === 'pending').length || 0;
    const approved = accessRequests?.filter((r: B2bAccessRequest) => r.status === 'approved').length || 0;
    const rejected = accessRequests?.filter((r: B2bAccessRequest) => r.status === 'rejected').length || 0;

    return { total, pending, approved, rejected };
  }, [accessRequests]);

  const colors = mode === 'dark' ? {
    bg: palette.background,
    surface: palette.surface,
    surfaceElevated: palette.surfaceElevated,
    border: palette.border,
    borderLight: palette.borderLight,
    textPrimary: palette.textPrimary,
    textSecondary: palette.textSecondary,
    textMuted: palette.textMuted,
  } : {
    bg: palette.light.background,
    surface: palette.light.surface,
    surfaceElevated: palette.light.surfaceElevated,
    border: palette.light.border,
    borderLight: palette.light.borderLight,
    textPrimary: palette.light.textPrimary,
    textSecondary: palette.light.textSecondary,
    textMuted: palette.light.textMuted,
  };

  return (
    <Box sx={{ ...animations.fadeIn }}>
      {/* Header */}
      <Box sx={{ mb: 4, ...animations.slideDown }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <PersonAddIcon sx={{ color: palette.primary, fontSize: 24 }} />
              <Typography
                variant="overline"
                sx={{
                  color: palette.primary,
                  fontFamily: typography.fontFamily.mono,
                  fontSize: typography.fontSize.xs,
                  letterSpacing: typography.letterSpacing.widest,
                  fontWeight: typography.fontWeight.bold,
                }}
              >
                B2B PORTAL
              </Typography>
            </Box>
            <Typography
              variant="h3"
              sx={{
                fontFamily: typography.fontFamily.display,
                fontWeight: typography.fontWeight.extrabold,
                color: colors.textPrimary,
                letterSpacing: '-0.02em',
                lineHeight: 1.1,
              }}
            >
              ACCESS REQUESTS
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Stats Dashboard */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 2, mb: 3, ...animations.slideUp(0.1) }}>
        {[
          { label: 'TOTAL', value: stats.total, color: palette.primary },
          { label: 'PENDING', value: stats.pending, color: palette.warning },
          { label: 'APPROVED', value: stats.approved, color: palette.success },
          { label: 'REJECTED', value: stats.rejected, color: palette.error },
        ].map((stat) => (
          <Card
            key={stat.label}
            sx={{
              backgroundColor: colors.surface,
              border: `2px solid ${colors.border}`,
              borderRadius: theme.radius.sm,
              p: 2.5,
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                backgroundColor: stat.color,
              },
            }}
          >
            <Typography
              variant="overline"
              sx={{
                color: colors.textMuted,
                fontFamily: typography.fontFamily.mono,
                fontSize: typography.fontSize.xs,
                letterSpacing: typography.letterSpacing.widest,
                fontWeight: typography.fontWeight.bold,
              }}
            >
              {stat.label}
            </Typography>
            <Typography
              variant="h3"
              sx={{
                fontFamily: typography.fontFamily.mono,
                fontWeight: typography.fontWeight.extrabold,
                color: stat.color,
                lineHeight: 1,
                mt: 1,
              }}
            >
              {stat.value}
            </Typography>
          </Card>
        ))}
      </Box>

      {/* Search & Filter Bar */}
      <Card
        sx={{
          backgroundColor: colors.surface,
          border: `2px solid ${colors.border}`,
          borderRadius: theme.radius.sm,
          p: 2,
          mb: 3,
          ...animations.slideUp(0.15),
        }}
      >
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            placeholder="SEARCH REQUESTS..."
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            sx={{
              flex: 1,
              minWidth: 300,
              '& .MuiOutlinedInput-root': {
                backgroundColor: colors.surfaceElevated,
                border: `1px solid ${colors.border}`,
                borderRadius: theme.radius.sm,
                fontFamily: typography.fontFamily.mono,
                fontSize: typography.fontSize.sm,
                '& fieldset': { border: 'none' },
              },
              '& .MuiInputBase-input': {
                color: colors.textPrimary,
                fontWeight: typography.fontWeight.medium,
                textTransform: 'uppercase',
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: colors.textMuted }} />
                </InputAdornment>
              ),
            }}
          />
          <TableToolbar
            title=""
            searchValue=""
            onSearchChange={() => {}}
            searchPlaceholder=""
            filters={filterOptions}
            activeFilters={activeFilters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
            data={filteredData}
            columns={exportColumns}
            exportFileName="access-requests"
          />
        </Box>
      </Card>

      {/* Table */}
      <Card
        sx={{
          backgroundColor: colors.surface,
          border: `2px solid ${colors.border}`,
          borderRadius: theme.radius.sm,
          overflow: 'hidden',
          ...animations.slideUp(0.2),
        }}
      >
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress sx={{ color: palette.primary }} />
          </Box>
        ) : filteredData.length === 0 ? (
          <Box sx={{ p: 8, textAlign: 'center' }}>
            <PersonAddIcon sx={{ fontSize: 80, color: colors.textMuted, mb: 2 }} />
            <Typography
              variant="h5"
              sx={{
                color: colors.textPrimary,
                fontFamily: typography.fontFamily.display,
                fontWeight: typography.fontWeight.bold,
                mb: 1,
              }}
            >
              NO ACCESS REQUESTS
            </Typography>
            <Typography variant="body2" sx={{ color: colors.textMuted }}>
              B2B access requests will appear here
            </Typography>
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table sx={{ '& .MuiTableCell-root': { borderBottom: `1px solid ${colors.border}` } }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: colors.surfaceElevated, borderBottom: `2px solid ${palette.primary}` }}>
                    <TableCell sx={{ color: colors.textMuted, fontFamily: typography.fontFamily.mono, fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, textTransform: 'uppercase' }}>
                      Company
                    </TableCell>
                    <TableCell sx={{ color: colors.textMuted, fontFamily: typography.fontFamily.mono, fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, textTransform: 'uppercase' }}>
                      Contact
                    </TableCell>
                    <TableCell sx={{ color: colors.textMuted, fontFamily: typography.fontFamily.mono, fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, textTransform: 'uppercase' }}>
                      Email
                    </TableCell>
                    <TableCell align="center" sx={{ color: colors.textMuted, fontFamily: typography.fontFamily.mono, fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, textTransform: 'uppercase' }}>
                      Status
                    </TableCell>
                    <TableCell sx={{ color: colors.textMuted, fontFamily: typography.fontFamily.mono, fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, textTransform: 'uppercase' }}>
                      Submitted
                    </TableCell>
                    <TableCell align="center" sx={{ color: colors.textMuted, fontFamily: typography.fontFamily.mono, fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, textTransform: 'uppercase' }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedData.map((request: B2bAccessRequest, index: number) => {
                    const statusColor = getStatusColor(request.status);

                    return (
                      <TableRow
                        key={request.id}
                        sx={{
                          borderLeft: `4px solid ${statusColor}`,
                          ...animations.tableRowFade(index),
                          '&:hover': {
                            backgroundColor: alpha(statusColor, 0.05),
                            transform: 'translateX(4px)',
                          },
                        }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <BusinessIcon sx={{ fontSize: 18, color: palette.primary }} />
                            <Typography
                              sx={{
                                fontFamily: typography.fontFamily.display,
                                fontWeight: typography.fontWeight.semibold,
                                color: colors.textPrimary,
                              }}
                            >
                              {request.companyName}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography sx={{ color: colors.textPrimary }}>
                            {request.firstName} {request.lastName}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography sx={{ color: colors.textSecondary, fontFamily: typography.fontFamily.mono, fontSize: typography.fontSize.sm }}>
                            {request.email}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            icon={getStatusIcon(request.status)}
                            label={request.status.toUpperCase()}
                            size="small"
                            sx={{
                              backgroundColor: alpha(statusColor, 0.1),
                              color: statusColor,
                              fontFamily: typography.fontFamily.mono,
                              fontSize: typography.fontSize.xs,
                              fontWeight: typography.fontWeight.bold,
                              border: `1px solid ${statusColor}`,
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography sx={{ color: colors.textMuted, fontSize: typography.fontSize.sm }}>
                            {new Date(request.createdAt).toLocaleDateString()}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          {request.status === 'pending' ? (
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                              <Button
                                size="small"
                                variant="contained"
                                onClick={() => handleOpenApproveDialog(request)}
                                sx={{
                                  backgroundColor: palette.success,
                                  fontSize: typography.fontSize.xs,
                                  fontWeight: typography.fontWeight.bold,
                                  '&:hover': { backgroundColor: alpha(palette.success, 0.8) },
                                }}
                              >
                                Approve
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleOpenRejectDialog(request)}
                                sx={{
                                  color: palette.error,
                                  borderColor: palette.error,
                                  fontSize: typography.fontSize.xs,
                                  fontWeight: typography.fontWeight.bold,
                                  '&:hover': { backgroundColor: alpha(palette.error, 0.1) },
                                }}
                              >
                                Reject
                              </Button>
                            </Box>
                          ) : (
                            <Typography sx={{ color: colors.textMuted, fontSize: typography.fontSize.xs }}>
                              Reviewed
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ backgroundColor: colors.surfaceElevated, borderTop: `2px solid ${colors.border}` }}>
              <TablePagination
                component="div"
                count={filteredData.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={pageSize}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 25, 50, 100]}
                sx={{
                  '& .MuiTablePagination-toolbar': { color: colors.textPrimary },
                }}
              />
            </Box>
          </>
        )}
      </Card>

      {/* Approve Dialog */}
      <Dialog open={approveDialogOpen} onClose={handleCloseDialogs} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontFamily: typography.fontFamily.display, fontWeight: typography.fontWeight.bold }}>
          Approve Access Request
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Approving access for <strong>{selectedRequest?.companyName}</strong> ({selectedRequest?.email})
          </Typography>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Assign Pricing Tier *</InputLabel>
            <Select
              value={selectedTierId}
              onChange={(e) => setSelectedTierId(e.target.value)}
              label="Assign Pricing Tier *"
            >
              {pricingTiers?.filter((t: PricingTier) => t.isActive).map((tier: PricingTier) => (
                <MenuItem key={tier.id} value={tier.id}>
                  {tier.tierName.toUpperCase()} - {tier.discountType === 'percentage' ? `${tier.discountValue}%` : `${tier.discountValue} DZD`} off
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Notes (optional)"
            value={reviewNotes}
            onChange={(e) => setReviewNotes(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialogs}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleApprove}
            disabled={!selectedTierId || approveMutation.isPending}
            sx={{ backgroundColor: palette.success }}
          >
            {approveMutation.isPending ? 'Approving...' : 'Approve'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onClose={handleCloseDialogs} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontFamily: typography.fontFamily.display, fontWeight: typography.fontWeight.bold }}>
          Reject Access Request
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Rejecting access for <strong>{selectedRequest?.companyName}</strong> ({selectedRequest?.email})
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Reason (optional)"
            value={reviewNotes}
            onChange={(e) => setReviewNotes(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialogs}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleReject}
            disabled={rejectMutation.isPending}
            sx={{ backgroundColor: palette.error }}
          >
            {rejectMutation.isPending ? 'Rejecting...' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
